import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, CreditCard, Check, Package, Ticket, X } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, sessionId, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    setApplyingDiscount(true);
    try {
      const response = await axios.post(`${API}/discounts/validate`, null, {
        params: { code: discountCode, subtotal: cart.subtotal }
      });
      
      setAppliedDiscount(response.data);
      toast.success(`Discount applied: ${response.data.description || response.data.code}`);
    } catch (error) {
      console.error('Error applying discount:', error);
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        customer_info: formData,
        cart_session_id: sessionId,
        discount_code: appliedDiscount?.code || null
      });
      
      setOrderNumber(response.data.order_number);
      setOrderComplete(true);
      await clearCart();
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const finalTotal = appliedDiscount ? appliedDiscount.new_total : cart.subtotal;

  if (orderComplete) {
    return (
      <div className="min-h-screen pt-28 pb-16" data-testid="order-complete">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-serif text-3xl font-semibold text-[#0A0A0A] mb-4">
            Order Confirmed!
          </h1>
          <p className="text-[#737373] mb-2">
            Thank you for your order. Your order number is:
          </p>
          <p className="text-xl font-semibold text-[#0A0A0A] mb-6" data-testid="order-number">
            {orderNumber}
          </p>
          <p className="text-[#737373] mb-8">
            We will contact you shortly to arrange payment via Paynow and confirm delivery details.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12 px-8 gap-2"
            >
              <Link to={`/track-order?order=${orderNumber}`}>
                <Package className="w-4 h-4" />
                Track Order
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="rounded-none h-12 px-8"
            >
              <Link to="/store">Continue Shopping</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-28 pb-16" data-testid="empty-checkout">
        <div className="max-w-lg mx-auto px-6 text-center">
          <h1 className="font-serif text-3xl font-semibold text-[#0A0A0A] mb-4">
            Your Cart is Empty
          </h1>
          <p className="text-[#737373] mb-8">
            Add some products to your cart before checking out.
          </p>
          <Button 
            asChild
            className="bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12 px-8"
          >
            <Link to="/store">Browse Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16" data-testid="checkout-page">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        {/* Header */}
        <Link 
          to="/store"
          className="inline-flex items-center text-sm text-[#737373] hover:text-[#0A0A0A] mb-8"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Store
        </Link>

        <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#0A0A0A] mb-12">
          Checkout
        </h1>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h2 className="font-serif text-xl font-semibold text-[#0A0A0A]">
                Contact Information
              </h2>
              
              <div>
                <Label htmlFor="name" className="text-sm text-[#737373]">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="mt-1 rounded-none h-12 border-[#E5E5E5]"
                  data-testid="checkout-name"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm text-[#737373]">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1 rounded-none h-12 border-[#E5E5E5]"
                  data-testid="checkout-email"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm text-[#737373]">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="mt-1 rounded-none h-12 border-[#E5E5E5]"
                  data-testid="checkout-phone"
                />
              </div>
            </div>

            <div className="space-y-4 pt-6">
              <h2 className="font-serif text-xl font-semibold text-[#0A0A0A]">
                Delivery Address
              </h2>
              
              <div>
                <Label htmlFor="address" className="text-sm text-[#737373]">Street Address *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="mt-1 rounded-none h-12 border-[#E5E5E5]"
                  data-testid="checkout-address"
                />
              </div>

              <div>
                <Label htmlFor="city" className="text-sm text-[#737373]">City *</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="mt-1 rounded-none h-12 border-[#E5E5E5]"
                  data-testid="checkout-city"
                />
              </div>

              <div>
                <Label htmlFor="notes" className="text-sm text-[#737373]">Order Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any special instructions for your order..."
                  className="mt-1 rounded-none border-[#E5E5E5] min-h-[100px]"
                  data-testid="checkout-notes"
                />
              </div>
            </div>

            {/* Payment Notice */}
            <div className="bg-[#FAFAFA] p-6 border border-[#E5E5E5]">
              <div className="flex items-start gap-4">
                <CreditCard className="w-6 h-6 text-[#0A0A0A] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#0A0A0A]">Payment via Paynow</h3>
                  <p className="text-sm text-[#737373] mt-1">
                    After placing your order, we will contact you with Paynow payment details. 
                    Your order will be confirmed once payment is received.
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none btn-lift text-sm uppercase tracking-wide"
              data-testid="place-order-btn"
            >
              {loading ? 'Processing...' : 'Place Order'}
            </Button>
          </form>

          {/* Order Summary */}
          <div className="lg:pl-12">
            <div className="bg-[#FAFAFA] p-8 sticky top-28">
              <h2 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 pb-6 border-b border-[#E5E5E5]">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex gap-4" data-testid={`summary-item-${item.product_id}`}>
                    <div className="w-16 h-20 bg-white overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#0A0A0A] text-sm truncate">{item.name}</h3>
                      <p className="text-sm text-[#737373]">Qty: {item.quantity}</p>
                      <p className="text-sm font-medium text-[#0A0A0A]">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 py-6 border-b border-[#E5E5E5]">
                <div className="flex justify-between text-sm">
                  <span className="text-[#737373]">Subtotal</span>
                  <span className="text-[#0A0A0A]">${cart.subtotal.toFixed(2)}</span>
                </div>
                
                {/* Discount Code Input */}
                {!appliedDiscount ? (
                  <div className="pt-2">
                    <Label className="text-sm text-[#737373]">Discount Code</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="rounded-none h-10 uppercase"
                      />
                      <Button
                        type="button"
                        onClick={handleApplyDiscount}
                        disabled={applyingDiscount}
                        variant="outline"
                        className="rounded-none h-10 px-4"
                      >
                        {applyingDiscount ? '...' : 'Apply'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-green-600" />
                      <div>
                        <span className="font-mono text-sm font-semibold text-green-700">{appliedDiscount.code}</span>
                        <p className="text-xs text-green-600">
                          {appliedDiscount.discount_type === 'percentage' 
                            ? `${appliedDiscount.discount_value}% off` 
                            : `$${appliedDiscount.discount_value.toFixed(2)} off`}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={handleRemoveDiscount}
                      className="p-1 hover:bg-green-100 rounded"
                    >
                      <X className="w-4 h-4 text-green-600" />
                    </button>
                  </div>
                )}

                {appliedDiscount && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-${appliedDiscount.discount_amount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-[#737373]">Shipping</span>
                  <span className="text-[#0A0A0A]">Calculated after order</span>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <span className="font-semibold text-[#0A0A0A]">Total</span>
                <span className="font-serif text-2xl font-semibold text-[#0A0A0A]" data-testid="checkout-total">
                  ${finalTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
